import { ChangeEvent, FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { TicketCategory, TicketPriority } from "@signalist/shared-types";

import { ReactComponent as MagnifyingGlass } from "@assets/icons/list-builder/magnifying-glass.svg";

import { AutoComplete } from "@/components/AutoComplete/AutoComplete";
import { ButtonSize } from "@/components/Button/types";
import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";
import { SvgIcon } from "@/components/Icon/SvgIcon";
import { InputField } from "@/components/InputField/InputField";
import { classNames } from "@/utils/classNames";

import { StaffTicketQueueController } from "../hooks/staffTicketQueueController";
import { useCompanySearch } from "../hooks/useCompanySearch";
import { AssigneeFilter, toggleStatus } from "../utils/staffQueueQuery";
import { ticketUserName } from "../utils/staffTicketRowMeta";
import { isAssigneeLocked, StaffTicketTab, statusOptionsForTab } from "../utils/staffTicketTabs";

const ANY = "ANY";
const UNASSIGNED = "UNASSIGNED";

const toAssigneeFilter = (id: AutoCompleteItem["id"]): AssigneeFilter =>
	id === ANY ? "ANY" : id === UNASSIGNED ? "UNASSIGNED" : Number(id);

/**
 * The filter row (mockup `.l3`). Presentational: every value lives in `useStaffTicketQueue` and
 * goes straight into the `tickets/admin` params. The status toggles offer the active tab's own
 * statuses (decision 5) and disappear on a single-status tab; the assignee control is locked on
 * Mine / Unassigned because the tab already decides it.
 */
export const StaffTicketFilterBar: FunctionComponent<{
	controller: StaffTicketQueueController;
}> = ({ controller }) => {
	const { t } = useTranslation();
	const companies = useCompanySearch();

	const statusOptions = statusOptionsForTab(controller.tab);
	const locked = isAssigneeLocked(controller.tab);

	// Memoised: the shared Dropdown re-selects from an effect whenever `data` changes identity.
	const assigneeOptions: AutoCompleteItem[] = useMemo(
		() => [
			{ id: ANY, title: t("support.staff.filters.assigneeAnyone") },
			{ id: UNASSIGNED, title: t("support.staff.filters.assigneeUnassigned") },
			...controller.assignees.map((user) => ({ id: user.id, title: ticketUserName(user) })),
		],
		[controller.assignees, t]
	);
	const lockedAssignee: AutoCompleteItem = {
		id: "LOCKED",
		title: t(
			controller.tab === StaffTicketTab.MINE
				? "support.staff.filters.assigneeMine"
				: "support.staff.filters.assigneeUnassigned"
		),
	};
	const selectedAssignee = locked
		? lockedAssignee
		: (assigneeOptions.find((option) => option.id === controller.filters.assignee) ??
			assigneeOptions[0]);

	const categoryOptions: AutoCompleteItem[] = useMemo(
		() => [
			{ id: null, title: t("support.staff.filters.categoryAll") },
			...Object.values(TicketCategory).map((value) => ({
				id: value,
				title: t(`support.category.${value}`),
			})),
		],
		[t]
	);
	const priorityOptions: AutoCompleteItem[] = useMemo(
		() => [
			{ id: null, title: t("support.staff.filters.priorityAll") },
			...Object.values(TicketPriority).map((value) => ({
				id: value,
				title: t(`support.staff.priority.${value}`),
			})),
		],
		[t]
	);

	return (
		<div className="flex min-h-11 flex-wrap items-center gap-2 border-b border-border-light bg-white px-4 py-1.5">
			<div className="w-[220px] flex-shrink-0">
				<InputField
					className="!rounded-[10px]"
					classNameWrapper="!mb-0"
					handleChange={(event: ChangeEvent<HTMLInputElement>) =>
						controller.setSearch(event.target.value)
					}
					iconInside={<SvgIcon className="text-gray-500" svgIcon={MagnifyingGlass} />}
					name="search"
					placeholder={t("support.staff.filters.searchPlaceholder")}
					size={ButtonSize.XS}
					value={controller.search}
				/>
			</div>

			{statusOptions.length > 1 && (
				<div
					aria-label={t("support.staff.filters.statusLabel")}
					className="flex items-center gap-1"
					role="group"
				>
					{statusOptions.map((status) => {
						const active = controller.filters.statuses.includes(status);

						return (
							<button
								key={status}
								aria-pressed={active}
								className={classNames(
									"h-8 cursor-pointer rounded-[10px] border px-2.5 text-xs font-semibold transition-colors duration-200",
									active
										? "border-transparent bg-blue-action-200 text-brand-light"
										: "border-gray-200 text-text-meta hover:border-brand-light"
								)}
								type="button"
								onClick={() =>
									controller.setFilters({
										statuses: toggleStatus(controller.filters.statuses, status),
									})
								}
							>
								{t(`support.staff.status.${status}`)}
							</button>
						);
					})}
				</div>
			)}

			<div className="flex-shrink-0">
				<Dropdown
					classNameDropdown="w-max min-w-[180px]"
					data={assigneeOptions}
					defaultValue={assigneeOptions[0]}
					disabled={locked}
					floating={true}
					handleSelect={(value: AutoCompleteItem) =>
						controller.setFilters({ assignee: toAssigneeFilter(value.id) })
					}
					size={ButtonSize.XS}
					value={selectedAssignee}
				/>
			</div>

			<div className="flex-shrink-0">
				<Dropdown
					classNameDropdown="w-max min-w-[180px]"
					data={categoryOptions}
					defaultValue={categoryOptions[0]}
					floating={true}
					handleSelect={(value: AutoCompleteItem) =>
						controller.setFilters({ category: (value.id as TicketCategory | null) ?? null })
					}
					size={ButtonSize.XS}
					value={
						categoryOptions.find((option) => option.id === controller.filters.category) ??
						categoryOptions[0]
					}
				/>
			</div>

			<div className="flex-shrink-0">
				<Dropdown
					classNameDropdown="w-max min-w-[160px]"
					data={priorityOptions}
					defaultValue={priorityOptions[0]}
					floating={true}
					handleSelect={(value: AutoCompleteItem) =>
						controller.setFilters({ priority: (value.id as TicketPriority | null) ?? null })
					}
					size={ButtonSize.XS}
					value={
						priorityOptions.find((option) => option.id === controller.filters.priority) ??
						priorityOptions[0]
					}
				/>
			</div>

			<div className="w-[220px] flex-shrink-0">
				<AutoComplete
					data={companies.options}
					disableLocalFilter
					floating
					handleReset={() => controller.setFilters({ companyId: null, companyName: null })}
					handleSearch={companies.search}
					handleSelect={(item) => {
						if (item) {
							controller.setFilters({ companyId: Number(item.id), companyName: item.title });
						}
					}}
					isLoading={companies.isLoading}
					placeholder={t("support.staff.filters.companyAll")}
					selectedItem={controller.companyLabel ?? ""}
				/>
			</div>
		</div>
	);
};
